import { startOfDay, endOfDay } from 'date-fns';
import { listStorefrontBusySlots } from '../../../../../../api/storefront/storefront';
import {
  canBookWithOverlapCount,
  countOverlappingAppointments,
  getDoubleBookingSettings,
} from '../../../../../../shared/booking/double-booking/employeeSlotAvailability';

const BUSY_SLOTS_CACHE_TTL_MS = 60_000;
const busySlotsCache = new Map();
const pendingBusySlotFetches = new Map();

const mapCallableSlotsToAppointments = (slots = []) =>
  slots.map((slot) => ({
    id: slot.id,
    employeeId: slot.employeeId,
    dateTime: new Date(slot.startTime),
    endTime: new Date(slot.endTime),
  }));

const buildCacheKey = (companyId, rangeStart, rangeEnd) =>
  `${companyId}|${rangeStart.toISOString()}|${rangeEnd.toISOString()}`;

const computeTotalDurationMinutes = ({
  duration,
  bufferTime = 0,
  addons = [],
}) =>
  duration +
  bufferTime +
  addons.reduce(
    (total, addon) =>
      total +
      (parseInt(addon.duration, 10) || 0) +
      (parseInt(addon.bufferTime, 10) || 0),
    0
  );

const resolveSlotWindow = (startTime, totalDuration) => {
  const slotStart = startTime instanceof Date ? startTime : new Date(startTime);
  const slotEnd = new Date(slotStart);
  slotEnd.setMinutes(slotEnd.getMinutes() + totalDuration);

  const queryStartDate = new Date(slotStart);
  queryStartDate.setMinutes(queryStartDate.getMinutes() - totalDuration);
  const queryEndDate = new Date(slotEnd);
  queryEndDate.setMinutes(queryEndDate.getMinutes() + totalDuration);

  return { slotStart, slotEnd, queryStartDate, queryEndDate };
};

class AvailabilityService {
  static getSettingsFromCompany(companyData) {
    return getDoubleBookingSettings(companyData);
  }

  static clearBusySlotsCache() {
    busySlotsCache.clear();
  }

  static async fetchBusySlotsInRange(companyId, rangeStart, rangeEnd) {
    const cacheKey = buildCacheKey(companyId, rangeStart, rangeEnd);
    const cached = busySlotsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < BUSY_SLOTS_CACHE_TTL_MS) {
      return cached.slots;
    }

    const inFlight = pendingBusySlotFetches.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const fetchPromise = (async () => {
      const slots = mapCallableSlotsToAppointments(
        await listStorefrontBusySlots({
          companyId,
          from: rangeStart,
          to: rangeEnd,
        })
      );
      busySlotsCache.set(cacheKey, { slots, timestamp: Date.now() });
      return slots;
    })().finally(() => {
      pendingBusySlotFetches.delete(cacheKey);
    });

    pendingBusySlotFetches.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  static async fetchDayAppointments(companyId, date) {
    return this.fetchBusySlotsInRange(companyId, startOfDay(date), endOfDay(date));
  }

  static async checkEmployeeAvailability(params) {
    const {
      companyId,
      employeeId,
      startTime,
      duration,
      bufferTime = 0,
      addons = [],
      allowDoubleBooking = false,
      maxConcurrentPerSlot = null,
    } = params;

    const totalDuration = computeTotalDurationMinutes({ duration, bufferTime, addons });
    const { slotStart, slotEnd, queryStartDate, queryEndDate } = resolveSlotWindow(
      startTime,
      totalDuration
    );

    const appointments = await this.fetchBusySlotsInRange(
      companyId,
      queryStartDate,
      queryEndDate
    );

    const overlappingCount = countOverlappingAppointments({
      appointments,
      employeeId,
      slotStart,
      slotEnd,
    });

    const firstConflict = appointments.find((apt) => {
      if (apt.employeeId !== employeeId) return false;
      return slotStart < apt.endTime && slotEnd > apt.dateTime;
    });

    const isAvailable = canBookWithOverlapCount(overlappingCount, {
      allowDoubleBooking,
      maxConcurrentPerSlot,
    });

    return {
      isAvailable,
      conflict: firstConflict
        ? {
            id: firstConflict.id,
            startTime: firstConflict.dateTime,
            endTime: firstConflict.endTime,
            employeeId: firstConflict.employeeId,
          }
        : null,
    };
  }

  static async checkAnyEmployeeAvailability(params) {
    const {
      companyId,
      employeeIds,
      startTime,
      duration,
      bufferTime = 0,
      addons = [],
      allowDoubleBooking = false,
      maxConcurrentPerSlot = null,
    } = params;

    const ids = Array.isArray(employeeIds) ? employeeIds.filter(Boolean) : [];
    if (!ids.length) {
      return { isAvailable: false, availableOptions: [] };
    }

    const totalDuration = computeTotalDurationMinutes({ duration, bufferTime, addons });
    const { slotStart, slotEnd, queryStartDate, queryEndDate } = resolveSlotWindow(
      startTime,
      totalDuration
    );

    const appointments = await this.fetchBusySlotsInRange(
      companyId,
      queryStartDate,
      queryEndDate
    );

    const availableOptions = [];
    for (const employeeId of ids) {
      try {
        const overlappingCount = countOverlappingAppointments({
          appointments,
          employeeId,
          slotStart,
          slotEnd,
        });

        const isAvailable = canBookWithOverlapCount(overlappingCount, {
          allowDoubleBooking,
          maxConcurrentPerSlot,
        });

        if (isAvailable) {
          availableOptions.push({ employeeId });
        }
      } catch (err) {
        console.error(`[AvailabilityService] Error checking employee ${employeeId}:`, err);
      }
    }

    return {
      isAvailable: availableOptions.length > 0,
      availableOptions,
    };
  }
}

export default AvailabilityService;
